import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';
import InstallateurManagement from '../installateur/Installateur';
import EquipePerformanceDetail from './EquipePerformanceDetail';
import VehiculeManagement from './VehiculeManagement';
import FinanceManagement from './FinanceManagement';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Swal from 'sweetalert2';
import './EquipesInstallationManagement.css';

const getApiBase = () => {
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : apiUrl + '/api';
};

const API_BASE = getApiBase();
const authHeader = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

const emptyEquipeForm = {
    id: null,
    chefEquipe: '',
    sousChefEquipe: '',
    technicien1: '',
    technicien2: '',
    typeEquipe: '',
    vehiculeId: ''
};

const EquipesInstallationManagement = ({ onNavigate, onLogout, currentPage }) => {
    const { hasPermission } = usePermissions();
    const [activeTab, setActiveTab] = useState('equipes'); // 'equipes', 'vehicules', 'depenses'
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const userType = localStorage.getItem('typeUser');

    // Data states
    const [equipes, setEquipes] = useState([]);
    const [vehicules, setVehicules] = useState([]);
    const [stats, setStats] = useState({ totalEquipes: 0, totalClientsInstalles: 0, totalKWcInstalles: 0 });
    
    // Form options
    const [users, setUsers] = useState([]);

    // Modals
    const [showEquipeModal, setShowEquipeModal] = useState(false);
    const [selectedPerformance, setSelectedPerformance] = useState(null);

    const [equipeForm, setEquipeForm] = useState(emptyEquipeForm);
    
    // --- Filters ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [preset, setPreset] = useState('all');

    const handlePresetChange = (e) => {
        const val = e.target.value;
        setPreset(val);
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();
        const d = now.getDate();

        const formatDate = (date) => {
            const tzOffset = date.getTimezoneOffset() * 60000;
            return new Date(date.getTime() - tzOffset).toISOString().split('T')[0];
        };

        if (val === 'all') {
            setStartDate('');
            setEndDate('');
        } else if (val === 'today') {
            const todayStr = formatDate(now);
            setStartDate(todayStr);
            setEndDate(todayStr);
        } else if (val === 'week') {
            const dayOfWeek = now.getDay() || 7;
            const startOfWeek = new Date(y, m, d - dayOfWeek + 1);
            setStartDate(formatDate(startOfWeek));
            setEndDate(formatDate(now));
        } else if (val === 'month') {
            const startOfMonth = new Date(y, m, 1);
            setStartDate(formatDate(startOfMonth));
            setEndDate(formatDate(now));
        } else if (val === 'year') {
            const startOfYear = new Date(y, 0, 1);
            setStartDate(formatDate(startOfYear));
            setEndDate(formatDate(now));
        }
    };

    const handleDateChange = (setter) => (e) => {
        setPreset('custom');
        setter(e.target.value);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let params = '';
            if (startDate || endDate) {
                params = `?startDate=${startDate}&endDate=${endDate}`;
            }

            if (activeTab === 'equipes') {
                const [resEq, resVeh, resUsers] = await Promise.all([
                    axios.get(`${API_BASE}/equipes-installation${params}`, authHeader()),
                    axios.get(`${API_BASE}/vehicules`, authHeader()),
                    axios.get(`${API_BASE}/users`, authHeader())
                ]);
                setEquipes(Array.isArray(resEq.data) ? resEq.data : []);
                setVehicules(Array.isArray(resVeh.data) ? resVeh.data : []);
                setUsers(resUsers.data.filter(u => u.typeUser === 'installateur'));

                if (userType === 'admin') {
                    try {
                        const resStats = await axios.get(`${API_BASE}/equipes-installation/stats`, authHeader());
                        setStats(resStats.data || { totalEquipes: 0, totalClientsInstalles: 0, totalKWcInstalles: 0 });
                    } catch (e) {
                        console.error('Stats loading error', e);
                    }
                }
            } else if (activeTab === 'vehicules') {
                const resVeh = await axios.get(`${API_BASE}/vehicules`, authHeader());
                setVehicules(Array.isArray(resVeh.data) ? resVeh.data : []);
                const resEq = await axios.get(`${API_BASE}/equipes-installation${params}`, authHeader());
                setEquipes(Array.isArray(resEq.data) ? resEq.data : []);
            }
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                onLogout();
            }
        } finally {
            setLoading(false);
        }
    }, [activeTab, userType, onLogout, startDate, endDate]);

    useEffect(() => {
        if (userType !== 'admin' && userType !== 'moderateur' && userType !== 'installateur') {
            onNavigate('dashboard');
            return;
        }
        if (userType === 'moderateur' && !hasPermission('equipes_installation', 'read')) {
            onNavigate('dashboard');
            return;
        }
        fetchData();
    }, [fetchData, userType, onNavigate, hasPermission]);

    const flashMsg = (msg, type = 'success') => {
        Swal.fire({
            title: type === 'success' ? 'Succès' : 'Erreur',
            text: msg,
            icon: type,
            timer: 3000,
            showConfirmButton: false
        });
    };

    // --- Actions Équipes ---
    const saveEquipe = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                chefEquipe: equipeForm.chefEquipe,
                sousChefEquipe: equipeForm.sousChefEquipe,
                technicien1: equipeForm.technicien1,
                technicien2: equipeForm.technicien2,
                typeEquipe: equipeForm.typeEquipe,
                vehiculeId: equipeForm.vehiculeId
            };
            
            if (equipeForm.id) {
                await axios.put(`${API_BASE}/equipes-installation/${equipeForm.id}`, payload, authHeader());
                flashMsg('Équipe modifiée avec succès');
            } else {
                await axios.post(`${API_BASE}/equipes-installation`, payload, authHeader());
                flashMsg('Équipe créée avec succès');
            }
            setShowEquipeModal(false);
            fetchData();
        } catch (err) {
            flashMsg(err.response?.data?.message || 'Erreur lors de la sauvegarde.', 'error');
        }
    };

    const deleteEquipe = async (id) => {
        const result = await Swal.fire({
            title: 'Supprimer l\'équipe ?',
            text: "Cette action est irréversible.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer !'
        });
        if (!result.isConfirmed) return;
        
        try {
            await axios.delete(`${API_BASE}/equipes-installation/${id}`, authHeader());
            flashMsg('Équipe supprimée avec succès');
            fetchData();
        } catch (err) {
            flashMsg(err.response?.data?.message || 'Erreur lors de la suppression.', 'error');
        }
    };

    const fetchPerformance = async (id, equipeDetails) => {
        try {
            const res = await axios.get(`${API_BASE}/equipes-installation/${id}/performance-details`, authHeader());
            setSelectedPerformance({ ...res.data, equipeDetails });
        } catch (err) {
            flashMsg('Erreur lors du chargement des performances.', 'error');
        }
    };

    return (
        <div className="eqi-page-root">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
            <div className="eqi-page-content">
                <div className="eqi-header-row">
                    <div>
                        <h1 className="eqi-title">Gestion des Équipes & Logistique</h1>
                        <p className="eqi-subtitle">Suivi des équipes terrain, véhicules et de leurs installations clients</p>
                    </div>
                </div>

                {!selectedPerformance && (
                    <div className="eqi-tabs">
                        <button className={`eqi-tab ${activeTab === 'equipes' ? 'active' : ''}`} onClick={() => setActiveTab('equipes')}>Équipes & Performances</button>
                        <button className={`eqi-tab ${activeTab === 'installateurs' ? 'active' : ''}`} onClick={() => setActiveTab('installateurs')}>Comptes Installateurs</button>
                        <button className={`eqi-tab ${activeTab === 'vehicules' ? 'active' : ''}`} onClick={() => setActiveTab('vehicules')}>Véhicules (Administratif)</button>
                        <button className={`eqi-tab ${activeTab === 'depenses' ? 'active' : ''}`} onClick={() => setActiveTab('depenses')}>Suivi des Dépenses</button>
                    </div>
                )}

                {loading ? (
                    <LoadingSpinner message="Chargement des données..." />
                ) : selectedPerformance ? (
                    <EquipePerformanceDetail 
                        performance={selectedPerformance} 
                        onBack={() => setSelectedPerformance(null)} 
                    />
                ) : (
                    <>
                        {/* --- TAB INSTALLATEURS --- */}
                        {activeTab === 'installateurs' && (
                            <InstallateurManagement isEmbedded={true} onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                        )}

                        {/* --- TAB ÉQUIPES --- */}
                        {activeTab === 'equipes' && (
                            <>
                                {userType === 'admin' && (
                                    <div className="eqi-kpi-grid">
                                        <div className="eqi-kpi-card eqi-kpi-blue">
                                            <p className="eqi-kpi-value">{stats.totalEquipes || 0}</p>
                                            <p className="eqi-kpi-label">Équipes Actives</p>
                                        </div>
                                        <div className="eqi-kpi-card eqi-kpi-green">
                                            <p className="eqi-kpi-value">{stats.totalClientsInstalles || 0}</p>
                                            <p className="eqi-kpi-label">Clients Installés</p>
                                        </div>
                                        <div className="eqi-kpi-card eqi-kpi-orange">
                                            <p className="eqi-kpi-value">{stats.totalKWcInstalles || 0}</p>
                                            <p className="eqi-kpi-label">Total kWc</p>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="eqi-filter-row" style={{ display: 'flex', gap: '15px', marginBottom: '20px', alignItems: 'center', background: '#fff', padding: '15px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>Filtre Période :</h3>
                                    
                                    <select value={preset} onChange={handlePresetChange} className="eqi-input" style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none', minWidth: '150px' }}>
                                        <option value="all">Toute la période</option>
                                        <option value="today">Aujourd'hui</option>
                                        <option value="week">Cette semaine</option>
                                        <option value="month">Ce mois</option>
                                        <option value="year">Cette année</option>
                                        <option value="custom">Personnalisé</option>
                                    </select>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                                        <input type="date" value={startDate} onChange={handleDateChange(setStartDate)} className="eqi-input" style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none' }} />
                                        <span style={{ fontWeight: 'bold', color: '#6b7280' }}>à</span>
                                        <input type="date" value={endDate} onChange={handleDateChange(setEndDate)} className="eqi-input" style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none' }} />
                                    </div>
                                </div>

                                {userType === 'admin' && equipes.length > 0 && (
                                    <div className="eqi-charts-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                                        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                            <h3 style={{ marginBottom: '15px', textAlign: 'center', color: '#374151', fontSize: '1rem' }}>Clients Installés par Équipe</h3>
                                            <ResponsiveContainer width="100%" height={250}>
                                                <PieChart>
                                                    <Pie data={equipes.filter(e => e.totalClients > 0)} dataKey="totalClients" nameKey="chefEquipe.nom" cx="50%" cy="50%" outerRadius={80} label>
                                                        {equipes.map((entry, index) => <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1'][index % 8]} />)}
                                                    </Pie>
                                                    <RechartsTooltip formatter={(value, name, props) => [value, props.payload.chefEquipe?.nom + ' ' + props.payload.chefEquipe?.prenom]} />
                                                    <Legend formatter={(value, entry) => entry.payload.chefEquipe?.nom} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                            <h3 style={{ marginBottom: '15px', textAlign: 'center', color: '#374151', fontSize: '1rem' }}>Puissance (kWc) par Équipe</h3>
                                            <ResponsiveContainer width="100%" height={250}>
                                                <BarChart data={equipes} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis dataKey="chefEquipe.nom" tick={{fontSize: 12}} />
                                                    <YAxis tick={{fontSize: 12}} />
                                                    <RechartsTooltip formatter={(value, name) => [value + ' kWc', 'Puissance']} labelFormatter={(label, payload) => payload[0]?.payload?.chefEquipe?.nom + ' ' + payload[0]?.payload?.chefEquipe?.prenom} />
                                                    <Bar dataKey="totalKWc" fill="#10b981" radius={[4, 4, 0, 0]} barSize={40} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                <div className="eqi-toolbar-row" style={{justifyContent: 'flex-end'}}>
                                    {(userType === 'admin' && hasPermission('equipes_installation', 'create')) && (
                                        <button className="eqi-btn eqi-btn-primary" onClick={() => { setEquipeForm(emptyEquipeForm); setShowEquipeModal(true); }}>
                                            Ajouter une équipe
                                        </button>
                                    )}
                                </div>
                                <div className="eqi-table-wrap">
                                    <table className="eqi-main-table">
                                        <thead>
                                            <tr>
                                                <th>Chef d'équipe</th>
                                                <th>Sous-chef</th>
                                                <th>Technicien 1</th>
                                                <th>Technicien 2</th>
                                                <th>Type équipe</th>
                                                <th>Matricule voiture</th>
                                                <th>Nbr Clients</th>
                                                <th>Puissance (kWc)</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {equipes.length === 0 && (
                                                <tr><td colSpan="9" className="eqi-empty-cell">Aucune équipe trouvée.</td></tr>
                                            )}
                                            {equipes.map((item) => (
                                                <tr key={item._id}>
                                                    <td><strong>{item.chefEquipe?.nom} {item.chefEquipe?.prenom}</strong></td>
                                                    <td>{item.sousChefEquipe || '—'}</td>
                                                    <td>{item.technicien1}</td>
                                                    <td>{item.technicien2 || '—'}</td>
                                                    <td><span className="eqi-type-pill">{item.typeEquipe}</span></td>
                                                    <td>{item.vehicule ? item.vehicule.matricule : '—'}</td>
                                                    <td style={{textAlign: 'center'}}><span className="eqi-stat-badge" style={{background:'#eff6ff',color:'#2563eb',padding:'4px 8px',borderRadius:'12px',fontWeight:'600'}}>{item.totalClients || 0}</span></td>
                                                    <td style={{textAlign: 'center', fontWeight:'bold', color:'#059669'}}>{Number(item.totalKWc || 0).toFixed(2)}</td>
                                                    <td>
                                                        <div className="eqi-action-row">
                                                            <button className="eqi-btn eqi-btn-secondary" onClick={() => fetchPerformance(item._id, item)}>
                                                                Perfs
                                                            </button>
                                                            {(userType === 'admin' && hasPermission('equipes_installation', 'update')) && (
                                                                <button className="eqi-btn eqi-btn-edit" onClick={() => {
                                                                    setEquipeForm({
                                                                        id: item._id,
                                                                        chefEquipe: item.chefEquipe?._id,
                                                                        sousChefEquipe: item.sousChefEquipe || '',
                                                                        technicien1: item.technicien1 || '',
                                                                        technicien2: item.technicien2 || '',
                                                                        typeEquipe: item.typeEquipe || '',
                                                                        vehiculeId: item.vehicule?._id || ''
                                                                    });
                                                                    setShowEquipeModal(true);
                                                                }}>
                                                                    Modifier
                                                                </button>
                                                            )}
                                                            {(userType === 'admin' && hasPermission('equipes_installation', 'delete')) && (
                                                                <button className="eqi-btn eqi-btn-delete" onClick={() => deleteEquipe(item._id)}>
                                                                    Supprimer
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {/* --- TAB VÉHICULES --- */}
                        {activeTab === 'vehicules' && (
                            <VehiculeManagement />
                        )}

                        {/* --- TAB DÉPENSES --- */}
                        {activeTab === 'depenses' && (
                            <FinanceManagement />
                        )}
                    </>
                )}
            </div>

            {/* --- MODALS --- */}
            {showEquipeModal && (
                <div className="eqi-modal-overlay">
                    <div className="eqi-modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="eqi-modal-header">
                            <h2>{equipeForm.id ? 'Modifier Équipe' : 'Nouvelle Équipe'}</h2>
                            <button className="eqi-close-btn" onClick={() => setShowEquipeModal(false)}>×</button>
                        </div>
                        <form className="eqi-form-grid" onSubmit={saveEquipe}>
                            <div className="eqi-form-group">
                                <label>Chef d'équipe (Installateur) *</label>
                                <select value={equipeForm.chefEquipe} onChange={e => setEquipeForm({...equipeForm, chefEquipe: e.target.value})} required>
                                    <option value="">Sélectionner</option>
                                    {users.map(u => <option key={u._id} value={u._id}>{u.nom} {u.prenom}</option>)}
                                </select>
                            </div>
                            <div className="eqi-form-group">
                                <label>Sous-chef (Nom statique)</label>
                                <input type="text" value={equipeForm.sousChefEquipe} onChange={e => setEquipeForm({...equipeForm, sousChefEquipe: e.target.value})} />
                            </div>
                            <div className="eqi-form-group">
                                <label>Technicien 1 * (Nom statique)</label>
                                <input type="text" value={equipeForm.technicien1} onChange={e => setEquipeForm({...equipeForm, technicien1: e.target.value})} required />
                            </div>
                            <div className="eqi-form-group">
                                <label>Technicien 2 (Nom statique)</label>
                                <input type="text" value={equipeForm.technicien2} onChange={e => setEquipeForm({...equipeForm, technicien2: e.target.value})} />
                            </div>
                            <div className="eqi-form-group">
                                <label>Type équipe *</label>
                                <input type="text" value={equipeForm.typeEquipe} onChange={e => setEquipeForm({...equipeForm, typeEquipe: e.target.value})} placeholder="Ex: Photovoltaïque BT" required />
                            </div>
                            <div className="eqi-form-group">
                                <label>Véhicule Assigné * (Obligatoire)</label>
                                <select value={equipeForm.vehiculeId} onChange={e => setEquipeForm({...equipeForm, vehiculeId: e.target.value})} required>
                                    <option value="">Sélectionner un véhicule</option>
                                    {vehicules.map(v => <option key={v._id} value={v._id}>{v.marque} - {v.matricule}</option>)}
                                </select>
                            </div>
                            <div className="eqi-form-actions eqi-form-group-full">
                                <button type="button" className="eqi-btn eqi-btn-secondary" onClick={() => setShowEquipeModal(false)}>Annuler</button>
                                <button type="submit" className="eqi-btn eqi-btn-primary">{equipeForm.id ? 'Enregistrer' : 'Créer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EquipesInstallationManagement;